/**
 * Small shared helpers with zero deps (like the rest of `@richrich/contracts`).
 */

/** Collapse an intersection into a single flat object type for readable hovers. */
type Prettify<T> = { [K in keyof T]: T[K] } & {};

/** Keys whose declared type admits `null`/`undefined` — these become optional. */
type NullishKeys<T> = {
  [K in keyof T]-?: undefined extends T[K] ? K : null extends T[K] ? K : never;
}[keyof T];

/**
 * The type of {@link dropNullish}'s result. A key whose declared type can be
 * `null`/`undefined` becomes **optional** (its nullish members can't survive the
 * copy); every other key stays **required**. This is what lets a *whole* object —
 * required and optional fields together — pass through one `dropNullish(...)` call
 * and still assign to a target that requires the non-nullish fields.
 *
 * The two halves map over key *unions* (not `keyof T` directly) so the mapping is
 * non-homomorphic and the required/optional modifiers are set here, not inherited.
 */
export type DropNullish<T> = Prettify<
  { [K in Exclude<keyof T, NullishKeys<T>>]: NonNullable<T[K]> } & {
    [K in NullishKeys<T>]?: NonNullable<T[K]>;
  }
>;

/**
 * Shallow-copy `obj`, dropping every key whose value is `null` or `undefined`.
 *
 * Replaces the repeated `...(x !== undefined ? { x } : {})` spread used to build
 * objects with optional fields under `exactOptionalPropertyTypes`. Because the
 * result type keeps non-nullish-typed keys required (see {@link DropNullish}), the
 * whole object can go through one call instead of spreading a nullish-only subset:
 *
 * ```ts
 * // one call — required + optional fields together:
 * const config = dropNullish({ dsl, from, to, feeBps, slippageBps });
 * ```
 */
export function dropNullish<T extends Record<string, unknown>>(obj: T): DropNullish<T> {
  const out: Record<string, unknown> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value !== null && value !== undefined) out[key] = value;
    }
  }
  return out as DropNullish<T>;
}
